/**
 * Which agent inputs are constrained to the project's domains, and how to turn
 * a picked domain (+ optional path) into the value the agent actually receives.
 *
 * Mirrors backend/config/domains.js — the server re-validates everything here,
 * so the two lists must stay in sync.
 */

export const MAX_COMPETITORS = 2;

const URL_INPUT_NAMES = ["url"];
const DOMAIN_INPUT_NAMES = [
  "domain",
  "target_domain",
  "competitor",
  "website",
  "site",
];
const DOMAIN_LIST_INPUT_NAMES = ["domains"];
const PROPERTY_INPUT_NAMES = ["property"];

/**
 * "url" | "domain" | "domainList" | "property", or null for a normal field.
 */
export function targetInputKind(inputName) {
  const name = String(inputName || "").toLowerCase();
  if (URL_INPUT_NAMES.includes(name)) return "url";
  if (DOMAIN_INPUT_NAMES.includes(name)) return "domain";
  if (DOMAIN_LIST_INPUT_NAMES.includes(name)) return "domainList";
  if (PROPERTY_INPUT_NAMES.includes(name)) return "property";
  return null;
}

/** Reduce user input to a bare hostname for display. Loose — the server is strict. */
export function toHostname(value) {
  if (typeof value !== "string") return "";
  return value
    .trim()
    .toLowerCase()
    .replace(/^sc-domain:/, "")
    .replace(/^[a-z][a-z0-9+.-]*:\/\//, "")
    .replace(/^[^@/]*@/, "")
    .split(/[/?#]/)[0]
    .replace(/:\d+$/, "")
    .replace(/^www\./, "")
    .replace(/\.$/, "");
}

/**
 * Tidy a path fragment the user typed into the suffix box: guarantees a single
 * leading slash and drops a stray domain if they pasted a whole URL.
 * Returns "" for an empty or root-only path.
 */
export function normalizePath(raw) {
  let path = String(raw || "").trim();
  if (!path) return "";
  // Someone pasted a full URL into the path box — keep only what follows the host.
  if (/^[a-z][a-z0-9+.-]*:\/\//i.test(path)) {
    const rest = path.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
    const slash = rest.indexOf("/");
    path = slash === -1 ? "" : rest.slice(slash);
  }
  if (!path || path === "/") return "";
  if (!path.startsWith("/")) path = `/${path}`;
  return path;
}

/**
 * Build the value sent to the agent for a constrained input.
 *
 * - url       → https://domain/path
 * - domain    → domain
 * - property  → https://domain/ or sc-domain:domain (propertyStyle)
 * - domainList→ handled by the caller (comma-joined hostnames)
 */
export function composeTargetValue(kind, domain, { path, propertyStyle } = {}) {
  if (!domain) return "";
  if (kind === "domain") return domain;
  if (kind === "property") {
    return propertyStyle === "sc-domain"
      ? `sc-domain:${domain}`
      : `https://${domain}/`;
  }
  return `https://${domain}${normalizePath(path)}`;
}

/** Split a stored value back into { domain, path } so a form can prefill. */
export function decomposeTargetValue(value) {
  const raw = String(value || "").trim();
  if (!raw) return { domain: "", path: "" };
  const domain = toHostname(raw);
  const withoutScheme = raw
    .replace(/^sc-domain:/i, "")
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "");
  const slash = withoutScheme.indexOf("/");
  const path = slash === -1 ? "" : normalizePath(withoutScheme.slice(slash));
  return { domain, path };
}

/**
 * Does the form's current selection target the project's main domain?
 *
 * Mirrors runTargetsMainDomain() in backend/config/domains.js, which is what
 * actually decides whether project context is sent. Used only to keep the run
 * form honest about what will be included.
 */
export function selectionTargetsMainDomain(inputs, params, project) {
  const mainDomain = project?.domain;
  if (!mainDomain) return true;
  const hosts = [];
  for (const input of inputs || []) {
    const kind = targetInputKind(input.name);
    if (!kind) continue;
    const raw = String(params?.[input.name] ?? "").trim();
    if (!raw) continue;
    const parts = kind === "domainList" ? raw.split(",") : [raw];
    for (const part of parts) {
      const host = toHostname(part);
      if (host) hosts.push(host);
    }
  }
  if (!hosts.length) return true;
  return hosts.includes(String(mainDomain).toLowerCase());
}

/**
 * Which of the project's domains a field should start on. Almost always the
 * main domain — except a field literally named "competitor", where defaulting
 * to the user's own site would be nonsense.
 */
export function defaultDomainForInput(inputName, project) {
  if (!project?.domain) return null;
  if (String(inputName || "").toLowerCase() === "competitor") {
    return project.competitors?.[0] || null;
  }
  return project.domain;
}

/** Human label for a domain in a dropdown: main domain vs competitor. */
export function domainRoleLabel(domain, project) {
  if (!project) return "";
  if (domain === project.domain) return "Main domain";
  const index = (project.competitors || []).indexOf(domain);
  return index === -1 ? "" : `Competitor ${index + 1}`;
}
